import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  QrCode, Plus, Clock, CheckCircle, XCircle, LogOut, LogIn,
  User, MapPin, Calendar, Phone, FileText, AlertTriangle, Camera, Search
} from 'lucide-react';
import QRCode from 'qrcode';
import { Scanner } from '@yudiel/react-qr-scanner';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PASS_TYPES = [
  { value: 'local_outing', label: 'Local Outing' },
  { value: 'home_visit', label: 'Home Visit' },
  { value: 'medical', label: 'Medical' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'other', label: 'Other' }
];

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  parent_approved: 'bg-orange-100 text-orange-800',
  approved: 'bg-green-100 text-green-800',
  warden_approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  used: 'bg-blue-100 text-blue-800',
  out: 'bg-blue-100 text-blue-800',
  completed: 'bg-purple-100 text-purple-800',
  expired: 'bg-gray-100 text-gray-800'
};

const STATUS_LABELS = {
  pending: 'Pending Parent Approval',
  parent_approved: 'Pending Warden Approval',
  approved: 'Approved',
  warden_approved: 'Approved',
  rejected: 'Rejected',
  used: 'Currently Out',
  out: 'Currently Out',
  completed: 'Returned',
  expired: 'Expired'
};

// QR Code Display Component
const QRCodeDisplay = ({ passCode, studentName }) => {
  const [qrDataUrl, setQrDataUrl] = useState('');
  
  useEffect(() => {
    if (passCode) {
      QRCode.toDataURL(passCode, { width: 200, margin: 2 })
        .then(url => setQrDataUrl(url))
        .catch(err => console.error('QR generation error:', err));
    }
  }, [passCode]);
  
  if (!qrDataUrl) return <div className="h-48 flex items-center justify-center">Generating QR...</div>;
  
  return (
    <div className="text-center">
      <img src={qrDataUrl} alt="Gate Pass QR" className="mx-auto" />
      <p className="mt-2 font-mono text-lg font-bold">{passCode}</p>
      <p className="text-sm text-muted-foreground">{studentName}</p>
    </div>
  );
};

// Student View Component
const StudentGatePassView = ({ getAuthHeader }) => {
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [isHosteller, setIsHosteller] = useState(null);
  const [form, setForm] = useState({
    pass_type: 'local_outing',
    destination: '',
    reason: '',
    expected_out_date: '',
    expected_out_time: '',
    expected_return_date: '',
    expected_return_time: '',
    emergency_contact: ''
  });

  useEffect(() => {
    fetchPasses();
  }, []);

  const fetchPasses = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/gate-pass/my-requests`, { headers: getAuthHeader() });
      setPasses(res.data || []);
      
      // Check if user is a hosteller by trying to fetch hostel info
      try {
        await axios.get(`${API_URL}/api/hostel/my-room`, { headers: getAuthHeader() });
        setIsHosteller(true);
      } catch (hostelError) {
        if (hostelError.response?.status === 404 || hostelError.response?.data?.detail?.includes('not assigned to any room')) {
          setIsHosteller(false);
        } else {
          setIsHosteller(true); // Assume hosteller if other error
        }
      }
    } catch (error) {
      console.error('Failed to fetch passes:', error);
      setPasses([]);
      // If gate pass fetch fails with hosteller error, mark as non-hosteller
      if (error.response?.data?.detail?.includes('Gate pass is only for hostel students')) {
        setIsHosteller(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is a hosteller before submitting
    if (isHosteller === false) {
      toast.error('Gate pass requests are only available for hostel students. Please contact the administration if you believe this is an error.');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        reason: form.reason,
        leave_type: form.pass_type,
        from_date: form.expected_out_date,
        to_date: form.expected_return_date,
        from_time: form.expected_out_time,
        to_time: form.expected_return_time,
        destination: form.destination,
        contact_during_leave: form.emergency_contact
      });
      await axios.post(`${API_URL}/api/gate-pass/request?${params}`, {}, { headers: getAuthHeader() });
      toast.success('Gate pass request submitted!');
      setCreateDialog(false);
      setForm({
        pass_type: 'local_outing', destination: '', reason: '',
        expected_out_date: '', expected_out_time: '',
        expected_return_date: '', expected_return_time: '', emergency_contact: ''
      });
      fetchPasses();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create request';
      if (errorMessage.includes('Gate pass is only for hostel students')) {
        toast.error('Gate pass requests are only available for hostel students. Please contact the administration if you believe this is an error.');
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const viewPass = (pass) => {
    setSelectedPass(pass);
    setViewDialog(true);
  };

  if (loading) {
    return (
      <DashboardLayout title="My Gate Passes">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  const pendingPasses = passes.filter(p => ['pending', 'parent_approved'].includes(p.status));
  const approvedPasses = passes.filter(p => ['approved', 'warden_approved', 'used', 'out'].includes(p.status));
  const historyPasses = passes.filter(p => ['completed', 'rejected', 'expired'].includes(p.status));

  return (
    <DashboardLayout title="My Gate Passes">
      <div className="space-y-6">
        {/* Create New Request */}
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Gate Pass Requests</h2>
          {isHosteller === false ? (
            <div className="text-right">
              <Button disabled className="opacity-50">
                <Plus className="h-4 w-4 mr-2" /> New Request
              </Button>
              <p className="text-sm text-red-600 mt-1">Only available for hostel students</p>
            </div>
          ) : (
            <Button onClick={() => setCreateDialog(true)} data-testid="create-gate-pass-btn" disabled={isHosteller === null}>
              <Plus className="h-4 w-4 mr-2" /> New Request
            </Button>
          )}
        </div>

        {/* Non-hosteller warning */}
        {isHosteller === false && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-orange-800">
                <AlertTriangle className="h-5 w-5" />
                <p className="font-medium">Gate Pass Not Available</p>
              </div>
              <p className="text-sm text-orange-700 mt-1">
                Gate pass requests are only available for hostel students. If you believe this is an error, please contact the administration.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Pending Requests */}
        {pendingPasses.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                Pending Approvals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingPasses.map(pass => (
                  <div key={pass.id} className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{pass.destination}</p>
                      <p className="text-sm text-muted-foreground">
                        {pass.expected_out_date} {pass.expected_out_time}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={STATUS_COLORS[pass.status]}>{STATUS_LABELS[pass.status]}</Badge>
                      <Button variant="outline" size="sm" onClick={() => viewPass(pass)}>View</Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Approved Passes - Show QR */}
        {approvedPasses.length > 0 && (
          <Card className="border-green-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Active Passes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {approvedPasses.map(pass => (
                  <div key={pass.id} className="p-4 border rounded-lg bg-green-50">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-semibold">{pass.destination}</p>
                        <p className="text-sm text-muted-foreground">{pass.reason}</p>
                      </div>
                      <Badge className={STATUS_COLORS[pass.status]}>{STATUS_LABELS[pass.status]}</Badge>
                    </div>
                    <div className="text-sm space-y-1 mb-3">
                      <p><Calendar className="h-3 w-3 inline mr-1" /> Out: {pass.expected_out_date} {pass.expected_out_time}</p>
                      <p><Calendar className="h-3 w-3 inline mr-1" /> Return: {pass.expected_return_date} {pass.expected_return_time}</p>
                    </div>
                    <Button onClick={() => viewPass(pass)} className="w-full">
                      <QrCode className="h-4 w-4 mr-2" /> Show QR Code
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* History */}
        <Card>
          <CardHeader>
            <CardTitle>History</CardTitle>
          </CardHeader>
          <CardContent>
            {historyPasses.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No history yet</p>
            ) : (
              <div className="space-y-2">
                {historyPasses.map(pass => (
                  <div key={pass.id} className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{pass.destination}</p>
                      <p className="text-sm text-muted-foreground">{pass.expected_out_date}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={STATUS_COLORS[pass.status]}>
                        {STATUS_LABELS[pass.status]}
                        {pass.is_late_return && ' (Late)'}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={() => viewPass(pass)}>Details</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>New Gate Pass Request</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Pass Type</Label>
                <Select value={form.pass_type} onValueChange={v => setForm({...form, pass_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PASS_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Destination *</Label>
                <Input value={form.destination} onChange={e => setForm({...form, destination: e.target.value})} required />
              </div>
              <div>
                <Label>Reason *</Label>
                <Textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Out Date *</Label>
                  <Input type="date" value={form.expected_out_date} onChange={e => setForm({...form, expected_out_date: e.target.value})} required />
                </div>
                <div>
                  <Label>Out Time *</Label>
                  <Input type="time" value={form.expected_out_time} onChange={e => setForm({...form, expected_out_time: e.target.value})} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Return Date *</Label>
                  <Input type="date" value={form.expected_return_date} onChange={e => setForm({...form, expected_return_date: e.target.value})} required />
                </div>
                <div>
                  <Label>Return Time *</Label>
                  <Input type="time" value={form.expected_return_time} onChange={e => setForm({...form, expected_return_time: e.target.value})} required />
                </div>
              </div>
              <div>
                <Label>Emergency Contact</Label>
                <Input value={form.emergency_contact} onChange={e => setForm({...form, emergency_contact: e.target.value})} placeholder="Phone number" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
                <Button type="submit">Submit Request</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Pass Dialog */}
        <Dialog open={viewDialog} onOpenChange={setViewDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Gate Pass Details</DialogTitle>
            </DialogHeader>
            {selectedPass && (
              <div className="space-y-4">
                {['approved', 'warden_approved', 'used', 'out'].includes(selectedPass.status) && selectedPass.pass_code && (
                  <div className="border-2 border-green-500 rounded-lg p-4">
                    <QRCodeDisplay passCode={selectedPass.pass_code} studentName={selectedPass.student_name} />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><Label>Status</Label><Badge className={STATUS_COLORS[selectedPass.status]}>{STATUS_LABELS[selectedPass.status]}</Badge></div>
                  <div><Label>Type</Label><p>{PASS_TYPES.find(t => t.value === selectedPass.pass_type)?.label || selectedPass.leave_type || 'N/A'}</p></div>
                  <div><Label>Destination</Label><p>{selectedPass.destination}</p></div>
                  <div><Label>Reason</Label><p>{selectedPass.reason}</p></div>
                  <div><Label>Out</Label><p>{selectedPass.expected_out_date || selectedPass.out_date || selectedPass.from_date} {selectedPass.expected_out_time || selectedPass.out_time || selectedPass.from_time}</p></div>
                  <div><Label>Return</Label><p>{selectedPass.expected_return_date || selectedPass.to_date} {selectedPass.expected_return_time || selectedPass.to_time}</p></div>
                  {selectedPass.parent_approval && (
                    <div><Label>Parent</Label><p className="text-green-600">✓ Approved</p></div>
                  )}
                  {selectedPass.warden_approval && (
                    <div><Label>Warden</Label><p className="text-green-600">✓ Approved</p></div>
                  )}
                  {(selectedPass.checkout_time || selectedPass.actual_out_time) && (
                    <div><Label>Actual Out</Label><p>{new Date(selectedPass.checkout_time || selectedPass.actual_out_time).toLocaleString()}</p></div>
                  )}
                  {(selectedPass.checkin_time || selectedPass.actual_in_time) && (
                    <div><Label>Actual In</Label><p>{new Date(selectedPass.checkin_time || selectedPass.actual_in_time).toLocaleString()}</p></div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Warden/Admin View Component
const WardenGatePassView = ({ getAuthHeader, user }) => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('pending');
  const [passes, setPasses] = useState([]);
  const [allPasses, setAllPasses] = useState([]);
  const [pendingPasses, setPendingPasses] = useState([]);
  const [dailyReport, setDailyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPass, setSelectedPass] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [scanDialog, setScanDialog] = useState(false);
  const [scanCode, setScanCode] = useState('');
  const [scannedPass, setScannedPass] = useState(null);
  const [showCamera, setShowCamera] = useState(false);

  // Auto-open scanner if URL has scanner=true parameter
  useEffect(() => {
    if (searchParams.get('scanner') === 'true') {
      setScanDialog(true);
      setShowCamera(true);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch pending approvals
      const pendingRes = await axios.get(`${API_URL}/api/gate-pass/pending-approvals`, { headers: getAuthHeader() });
      const pendingData = pendingRes.data || [];
      setPendingPasses(pendingData);
      setPasses(pendingData);
      
      // Fetch ALL gate passes for the school
      const allRes = await axios.get(`${API_URL}/api/gate-pass/all`, { headers: getAuthHeader() });
      const allData = allRes.data?.items || allRes.data?.passes || allRes.data || [];
      setAllPasses(allData);
      
      // Daily report with proper summary structure
      setDailyReport({ 
        summary: {
          total: allData.length,
          pending: allData.filter(p => p.status === 'pending' || p.status === 'parent_approved').length,
          approved: allData.filter(p => p.status === 'approved').length,
          currently_out: allData.filter(p => p.status === 'used').length,
          returned: allData.filter(p => p.status === 'completed').length,
          rejected: allData.filter(p => p.status === 'rejected').length,
          expired: allData.filter(p => p.status === 'expired').length,
          late_returns: allData.filter(p => p.is_late_return).length
        }
      });
    } catch (error) {
      console.error('Failed to fetch:', error);
      setPendingPasses([]);
      setAllPasses([]);
      setDailyReport({ summary: { total: 0, pending: 0, approved: 0, currently_out: 0, returned: 0, rejected: 0, expired: 0, late_returns: 0 } });
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (passId, approved, remarks = '') => {
    try {
      if (approved) {
        await axios.post(`${API_URL}/api/gate-pass/${passId}/approve?remarks=${encodeURIComponent(remarks)}`, {}, { headers: getAuthHeader() });
        toast.success('Approved!');
      } else {
        await axios.post(`${API_URL}/api/gate-pass/${passId}/reject?reason=${encodeURIComponent(remarks || 'Rejected by approver')}`, {}, { headers: getAuthHeader() });
        toast.success('Rejected');
      }
      fetchData();
      setViewDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleEmergencyApprove = async (passId) => {
    try {
      await axios.post(`${API_URL}/api/gate-pass/${passId}/approve?remarks=Emergency%20approval`, {}, { headers: getAuthHeader() });
      toast.success('Emergency approved!');
      fetchData();
      setViewDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleScan = async () => {
    if (!scanCode) return;
    handleScanCode(scanCode);
  };

  const handleScanCode = async (code) => {
    if (!code) return;
    try {
      const res = await axios.get(`${API_URL}/api/hostel/gate-pass/scan/${code}`, { headers: getAuthHeader() });
      setScannedPass(res.data);
      toast.success('Pass found!');
    } catch (error) {
      toast.error('Invalid pass code');
      setScannedPass(null);
    }
  };

  const recordOut = async () => {
    if (!scannedPass) return;
    try {
      await axios.post(`${API_URL}/api/hostel/gate-pass/scan/${scannedPass.pass_code}/out`, {}, { headers: getAuthHeader() });
      toast.success('Marked as OUT');
      setScanDialog(false);
      setScanCode('');
      setScannedPass(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const recordIn = async () => {
    if (!scannedPass) return;
    try {
      const res = await axios.post(`${API_URL}/api/hostel/gate-pass/scan/${scannedPass.pass_code}/in`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setScanDialog(false);
      setScanCode('');
      setScannedPass(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  return (
    <DashboardLayout title="Gate Pass Management">
      <div className="space-y-6">
        {/* Summary Cards */}
        {dailyReport && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{dailyReport.summary.total}</p>
                <p className="text-xs text-muted-foreground">Total Today</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{dailyReport.summary.pending}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{dailyReport.summary.approved}</p>
                <p className="text-xs text-muted-foreground">Approved</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{dailyReport.summary.currently_out}</p>
                <p className="text-xs text-muted-foreground">Currently Out</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{dailyReport.summary.returned}</p>
                <p className="text-xs text-muted-foreground">Returned</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{dailyReport.summary.rejected}</p>
                <p className="text-xs text-muted-foreground">Rejected</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-orange-600">{dailyReport.summary.late_returns}</p>
                <p className="text-xs text-muted-foreground">Late Returns</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Scanner Button */}
        <div className="flex justify-end">
          <Button onClick={() => setScanDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <QrCode className="h-4 w-4 mr-2" /> Gate Scanner
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending">Pending Approval ({pendingPasses.length})</TabsTrigger>
            <TabsTrigger value="all">All Passes ({allPasses.length})</TabsTrigger>
            <TabsTrigger value="today">Today's Passes</TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <Card>
              <CardContent className="pt-6">
                {pendingPasses.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No pending approvals</p>
                ) : (
                  <div className="space-y-3">
                    {pendingPasses.map(pass => (
                      <div key={pass.id} className="flex justify-between items-center p-4 border rounded-lg hover:bg-muted/50">
                        <div>
                          <p className="font-semibold">{pass.student_name}</p>
                          <p className="text-sm">{pass.destination} - {pass.reason}</p>
                          <p className="text-xs text-muted-foreground">
                            {pass.expected_out_date || pass.out_date || pass.from_date} {pass.expected_out_time || pass.out_time || pass.from_time} → {pass.expected_return_date || pass.to_date} {pass.expected_return_time || pass.to_time}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Badge className={STATUS_COLORS[pass.status]}>{STATUS_LABELS[pass.status]}</Badge>
                          <Button variant="outline" size="sm" onClick={() => { setSelectedPass(pass); setViewDialog(true); }}>
                            Review
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle>All Gate Passes</CardTitle>
                <CardDescription>Complete history of all gate pass requests</CardDescription>
              </CardHeader>
              <CardContent>
                {allPasses.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No gate passes found</p>
                ) : (
                  <div className="space-y-3">
                    {allPasses.map(pass => (
                      <div key={pass.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold">{pass.student_name} <span className="text-muted-foreground font-normal">({pass.class_name})</span></p>
                            <p className="text-sm">{pass.destination}</p>
                            <p className="text-xs text-muted-foreground">{pass.reason}</p>
                          </div>
                          <Badge className={STATUS_COLORS[pass.status]}>{STATUS_LABELS[pass.status]}</Badge>
                        </div>
                        
                        {/* Schedule */}
                        <div className="grid grid-cols-2 gap-2 text-sm mt-2 p-2 bg-gray-50 rounded">
                          <div>
                            <span className="text-muted-foreground">Scheduled Out:</span>
                            <p className="font-medium">{pass.expected_out_date || pass.out_date || pass.from_date} {pass.expected_out_time || pass.out_time || pass.from_time}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Scheduled Return:</span>
                            <p className="font-medium">{pass.expected_return_date || pass.to_date} {pass.expected_return_time || pass.to_time}</p>
                          </div>
                        </div>
                        
                        {/* Actual In/Out times */}
                        {(pass.checkout_time || pass.checkin_time) && (
                          <div className="grid grid-cols-2 gap-2 text-sm mt-2 p-2 bg-blue-50 rounded">
                            {pass.checkout_time && (
                              <div>
                                <span className="text-blue-600 flex items-center gap-1">
                                  <LogOut className="h-3 w-3" /> Actual Out:
                                </span>
                                <p className="font-medium">{new Date(pass.checkout_time).toLocaleString()}</p>
                              </div>
                            )}
                            {pass.checkin_time && (
                              <div>
                                <span className="text-green-600 flex items-center gap-1">
                                  <LogIn className="h-3 w-3" /> Actual Return:
                                </span>
                                <p className="font-medium">{new Date(pass.checkin_time).toLocaleString()}</p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Approval info & Actions */}
                        <div className="flex justify-between items-center mt-2">
                          <div className="flex gap-4 text-xs text-muted-foreground">
                            {pass.parent_approval && (
                              <span className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3 text-green-500" /> Parent
                              </span>
                            )}
                            {pass.warden_approval && (
                              <span className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3 text-green-500" /> Warden
                              </span>
                            )}
                          </div>
                          {pass.status === 'approved' && (
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={async () => {
                                try {
                                  await axios.post(`${API_URL}/api/gate-pass/${pass.id}/checkout`, {}, { headers: getAuthHeader() });
                                  toast.success('Marked as OUT');
                                  fetchData();
                                } catch (e) { toast.error('Failed'); }
                              }}>
                                <LogOut className="h-3 w-3 mr-1" /> Mark Out
                              </Button>
                            </div>
                          )}
                          {pass.status === 'used' && (
                            <Button size="sm" variant="outline" onClick={async () => {
                              try {
                                await axios.post(`${API_URL}/api/gate-pass/${pass.id}/checkin`, {}, { headers: getAuthHeader() });
                                toast.success('Marked as Returned');
                                fetchData();
                              } catch (e) { toast.error('Failed'); }
                            }}>
                              <LogIn className="h-3 w-3 mr-1" /> Mark Return
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="today">
            <Card>
              <CardContent className="pt-6">
                {passes.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No passes for today</p>
                ) : (
                  <div className="space-y-3">
                    {passes.map(pass => (
                      <div key={pass.id} className="flex justify-between items-center p-4 border rounded-lg">
                        <div>
                          <p className="font-semibold">{pass.student_name} <span className="text-muted-foreground font-normal">({pass.student_class})</span></p>
                          <p className="text-sm">{pass.destination}</p>
                          <p className="text-xs text-muted-foreground">
                            Out: {pass.expected_out_time} | Return: {pass.expected_return_time}
                          </p>
                        </div>
                        <Badge className={STATUS_COLORS[pass.status]}>
                          {STATUS_LABELS[pass.status]}
                          {pass.is_late_return && ' (Late)'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Review Dialog */}
        <Dialog open={viewDialog} onOpenChange={setViewDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Review Gate Pass Request</DialogTitle>
            </DialogHeader>
            {selectedPass && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Student</Label>
                    <p className="font-semibold">{selectedPass.student_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedPass.student_class} | {selectedPass.student_roll}</p>
                  </div>
                  <div>
                    <Label>Room</Label>
                    <p>{selectedPass.hostel_room || 'N/A'}</p>
                  </div>
                  <div>
                    <Label>Type</Label>
                    <p>{PASS_TYPES.find(t => t.value === selectedPass.pass_type)?.label}</p>
                  </div>
                  <div>
                    <Label>Contact</Label>
                    <p>{selectedPass.emergency_contact || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <Label>Destination</Label>
                    <p>{selectedPass.destination}</p>
                  </div>
                  <div className="col-span-2">
                    <Label>Reason</Label>
                    <p>{selectedPass.reason}</p>
                  </div>
                  <div>
                    <Label>Out</Label>
                    <p>{selectedPass.expected_out_date} {selectedPass.expected_out_time}</p>
                  </div>
                  <div>
                    <Label>Return</Label>
                    <p>{selectedPass.expected_return_date} {selectedPass.expected_return_time}</p>
                  </div>
                  {selectedPass.parent_approval && (
                    <div className="col-span-2">
                      <Label>Parent Approval</Label>
                      <Badge className={selectedPass.parent_approval === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {selectedPass.parent_approval}
                      </Badge>
                      {selectedPass.parent_remarks && <p className="text-sm mt-1">{selectedPass.parent_remarks}</p>}
                    </div>
                  )}
                </div>
                {/* Show approve/reject buttons for pending or parent_approved status */}
                {['pending', 'parent_approved', 'pending_warden'].includes(selectedPass.status) && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button onClick={() => handleApproval(selectedPass.id, true)} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="approve-gate-pass-btn">
                      <CheckCircle className="h-4 w-4 mr-2" /> Approve
                    </Button>
                    <Button onClick={() => handleApproval(selectedPass.id, false)} variant="destructive" className="flex-1" data-testid="reject-gate-pass-btn">
                      <XCircle className="h-4 w-4 mr-2" /> Reject
                    </Button>
                  </div>
                )}
                {/* Emergency approve for passes waiting on parent approval */}
                {selectedPass.status === 'pending' && !selectedPass.parent_approval && (
                  <div className="pt-4 border-t">
                    <Button onClick={() => handleEmergencyApprove(selectedPass.id)} variant="outline" className="w-full border-orange-500 text-orange-600" data-testid="emergency-approve-btn">
                      <AlertTriangle className="h-4 w-4 mr-2" /> Emergency Approve (Skip Parent)
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Scanner Dialog */}
        <Dialog open={scanDialog} onOpenChange={(open) => {
          setScanDialog(open);
          if (!open) {
            setShowCamera(false);
            setScanCode('');
            setScannedPass(null);
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <QrCode className="h-5 w-5" /> Gate Scanner
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Camera Scanner */}
              {showCamera && (
                <div className="relative rounded-lg overflow-hidden bg-black">
                  <Scanner
                    onScan={(result) => {
                      if (result && result[0]) {
                        const code = result[0].rawValue;
                        setScanCode(code);
                        setShowCamera(false);
                        // Auto-search after scan
                        handleScanCode(code);
                      }
                    }}
                    onError={(error) => {
                      console.error('Scanner error:', error);
                      toast.error('Camera error - Please enter code manually');
                      setShowCamera(false);
                    }}
                    formats={['qr_code']}
                    components={{
                      audio: false,
                      finder: true
                    }}
                    styles={{
                      container: { height: '250px' },
                      video: { objectFit: 'cover' }
                    }}
                  />
                  <Button 
                    variant="secondary" 
                    size="sm"
                    className="absolute bottom-2 left-1/2 -translate-x-1/2"
                    onClick={() => setShowCamera(false)}
                  >
                    Cancel Scan
                  </Button>
                </div>
              )}
              
              {!showCamera && (
                <>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Enter or scan pass code" 
                      value={scanCode} 
                      onChange={e => setScanCode(e.target.value.toUpperCase())}
                      className="font-mono"
                    />
                    <Button onClick={handleScan}>
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setShowCamera(true)}
                  >
                    <Camera className="h-4 w-4 mr-2" /> Use Camera to Scan
                  </Button>
                </>
              )}

              {scannedPass && (
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-lg">{scannedPass.student_name}</p>
                      <p className="text-sm text-muted-foreground">{scannedPass.student_class}</p>
                    </div>
                    <Badge className={STATUS_COLORS[scannedPass.status]}>{STATUS_LABELS[scannedPass.status]}</Badge>
                  </div>
                  <div className="text-sm">
                    <p><MapPin className="h-3 w-3 inline mr-1" /> {scannedPass.destination}</p>
                    <p><Clock className="h-3 w-3 inline mr-1" /> Out: {scannedPass.expected_out_time} | Return: {scannedPass.expected_return_time}</p>
                  </div>

                  <div className="flex gap-2 pt-2">
                    {scannedPass.status === 'approved' && (
                      <Button onClick={recordOut} className="flex-1 bg-blue-600 hover:bg-blue-700">
                        <LogOut className="h-4 w-4 mr-2" /> Mark OUT
                      </Button>
                    )}
                    {scannedPass.status === 'out' && (
                      <Button onClick={recordIn} className="flex-1 bg-green-600 hover:bg-green-700">
                        <LogIn className="h-4 w-4 mr-2" /> Mark IN
                      </Button>
                    )}
                    {!['approved', 'out'].includes(scannedPass.status) && (
                      <p className="text-center text-muted-foreground w-full py-2">
                        Cannot record - Status: {STATUS_LABELS[scannedPass.status]}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Parent View Component
const ParentGatePassView = ({ getAuthHeader }) => {
  const [pendingPasses, setPendingPasses] = useState([]);
  const [allPasses, setAllPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPass, setSelectedPass] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    fetchPasses();
  }, []);

  const fetchPasses = async () => {
    try {
      // Fetch pending approvals
      const pendingRes = await axios.get(`${API_URL}/api/gate-pass/pending-approvals`, { headers: getAuthHeader() });
      setPendingPasses(pendingRes.data || []);
      
      // Fetch ALL passes for children (including approved, completed, etc.)
      const allRes = await axios.get(`${API_URL}/api/gate-pass/children-passes`, { headers: getAuthHeader() });
      setAllPasses(allRes.data || []);
    } catch (error) {
      console.error('Failed to fetch:', error);
      setPendingPasses([]);
      setAllPasses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (passId, approved) => {
    try {
      if (approved) {
        await axios.post(`${API_URL}/api/gate-pass/${passId}/approve?remarks=${encodeURIComponent(remarks || '')}`, {}, { headers: getAuthHeader() });
        toast.success('Approved!');
      } else {
        await axios.post(`${API_URL}/api/gate-pass/${passId}/reject?reason=${encodeURIComponent(remarks || 'Rejected by parent')}`, {}, { headers: getAuthHeader() });
        toast.success('Rejected');
      }
      fetchPasses();
      setViewDialog(false);
      setRemarks('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Gate Pass Approvals">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Gate Pass Approvals">
      <div className="space-y-6">
        {/* Pending Approvals */}
        {pendingPasses.length > 0 && (
          <Card className="border-yellow-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                Pending Your Approval ({pendingPasses.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingPasses.map(pass => (
                  <div key={pass.id} className="p-4 border rounded-lg bg-yellow-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold">{pass.student_name}</p>
                        <p className="text-sm">{pass.destination}</p>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{pass.reason}</p>
                    <p className="text-sm mb-3">
                      <Calendar className="h-3 w-3 inline mr-1" />
                      {pass.expected_out_date} {pass.expected_out_time} → {pass.expected_return_date} {pass.expected_return_time}
                    </p>
                    <div className="flex gap-2">
                      <Button onClick={() => handleApproval(pass.id, true)} className="flex-1 bg-green-600 hover:bg-green-700" size="sm">
                        <CheckCircle className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button onClick={() => { setSelectedPass(pass); setViewDialog(true); }} variant="destructive" className="flex-1" size="sm">
                        <XCircle className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Passes - History with In/Out tracking */}
        <Card>
          <CardHeader>
            <CardTitle>All Gate Passes</CardTitle>
            <CardDescription>Complete history of gate pass requests</CardDescription>
          </CardHeader>
          <CardContent>
            {allPasses.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No gate passes yet</p>
            ) : (
              <div className="space-y-3">
                {allPasses.map(pass => (
                  <div key={pass.id} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold">{pass.student_name}</p>
                        <p className="text-sm text-muted-foreground">{pass.destination}</p>
                        <p className="text-xs text-muted-foreground">{pass.reason}</p>
                      </div>
                      <Badge className={STATUS_COLORS[pass.status]}>{STATUS_LABELS[pass.status]}</Badge>
                    </div>
                    
                    {/* Schedule */}
                    <div className="grid grid-cols-2 gap-2 text-sm mt-2 p-2 bg-gray-50 rounded">
                      <div>
                        <span className="text-muted-foreground">Scheduled Out:</span>
                        <p className="font-medium">{pass.expected_out_date || pass.out_date || pass.from_date} {pass.expected_out_time || pass.out_time || pass.from_time}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Scheduled Return:</span>
                        <p className="font-medium">{pass.expected_return_date || pass.to_date} {pass.expected_return_time || pass.to_time}</p>
                      </div>
                    </div>
                    
                    {/* Actual In/Out times */}
                    {(pass.checkout_time || pass.checkin_time) && (
                      <div className="grid grid-cols-2 gap-2 text-sm mt-2 p-2 bg-blue-50 rounded">
                        {pass.checkout_time && (
                          <div>
                            <span className="text-blue-600 flex items-center gap-1">
                              <LogOut className="h-3 w-3" /> Actual Out:
                            </span>
                            <p className="font-medium">{new Date(pass.checkout_time).toLocaleString()}</p>
                          </div>
                        )}
                        {pass.checkin_time && (
                          <div>
                            <span className="text-green-600 flex items-center gap-1">
                              <LogIn className="h-3 w-3" /> Actual Return:
                            </span>
                            <p className="font-medium">{new Date(pass.checkin_time).toLocaleString()}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Approval info */}
                    <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                      {pass.parent_approval && (
                        <span className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 text-green-500" /> Parent Approved
                        </span>
                      )}
                      {pass.warden_approval && (
                        <span className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 text-green-500" /> Warden Approved
                        </span>
                      )}
                      {pass.warden_remarks && (
                        <span>Remarks: {pass.warden_remarks}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reject Dialog */}
        <Dialog open={viewDialog} onOpenChange={setViewDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Gate Pass</DialogTitle>
            </DialogHeader>
            {selectedPass && (
              <div className="space-y-4">
                <p>Rejecting pass for <strong>{selectedPass.student_name}</strong> to {selectedPass.destination}</p>
                <div>
                  <Label>Reason for rejection</Label>
                  <Textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Enter reason..." />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setViewDialog(false)}>Cancel</Button>
                  <Button variant="destructive" onClick={() => handleApproval(selectedPass.id, false)}>Reject</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Main Component - Role-based routing
const GatePassPage = () => {
  const { getAuthHeader, user } = useAuth();

  if (user?.role === 'student') {
    return <StudentGatePassView getAuthHeader={getAuthHeader} />;
  }

  if (user?.role === 'parent') {
    return <ParentGatePassView getAuthHeader={getAuthHeader} />;
  }

  // Admin/Teacher (Warden)
  return <WardenGatePassView getAuthHeader={getAuthHeader} user={user} />;
};

export default GatePassPage;
